import { Alert, AlertDescription, AlertTitle, Label } from '@folio/components';
import { Sheet, SheetContent, SheetTitle } from '@folio/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@folio/components/ui/tabs';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { DeleteDocumentDialog } from '../DeleteDocumentDialog';
import {
  parseAIClassificationMetadata,
  translateComplianceMessage,
} from '../DocumentClassificationStatus';
import { DocumentClassificationSelector } from './DocumentClassificationSelector';
import { DocumentReviewAction } from '../DocumentReviewAction';
import { DocumentClassificationButton } from '../../../forms/document-classification-button';
import { DocumentViewerHeader } from '../DocumentViewerHeader';
import { useConformityReasonPopover } from '../useConformityReasonPopover';
import { FraudAnalysisSection } from './FraudAnalysisSection';
import { ImageViewer } from '../ImageViewer';
import { PDFViewer } from '../PDFViewer';
import {
  ExtractionFieldsDisplay,
  type ReportedFieldInfo,
} from '../../document-extraction/ExtractionFieldsDisplay';
import {
  DocumentCoherenceTable,
  countCoherenceMismatches,
} from '../../document-coherence/legacy/DocumentCoherenceTable';
import type { GetDocumentExtractionOutput, SubmitFeedbackBody } from 'korint-sdk';
import { documentExtractionControllerGetExtractionFeedbackQueryOptions } from 'korint-sdk';
import { useQuery } from '@tanstack/react-query';
import type {
  DocumentConformityViewerState,
  DocumentViewerProps,
  DocumentType,
  DocumentViewerDocument,
} from '../types';
import { useTranslation } from 'react-i18next';
import { Tooltip, TooltipContent, TooltipTrigger } from '@folio/components/ui/tooltip';
import type { ValidityRuleResult } from 'korint-sdk/react';
import {
  AlertTriangleIcon,
  ArrowRightLeftIcon,
  CheckCircle2Icon,
  CheckIcon,
  FlagIcon,
  Loader2Icon,
  PenIcon,
  RotateCwIcon,
  XCircleIcon,
  XIcon,
} from 'lucide-react';
import { cn, formatDate } from '@folio/lib';
import { hasOtherCategory } from '@folio/utils/document-categories';
import { getDocumentCategoryLabel } from '@folio/utils/getDocumentCategoryLabel';
import { Popover, PopoverContent, PopoverTrigger } from '@folio/components/ui/popover';
import { Button } from '@folio/components/ui/button';
import { Textarea } from '@folio/components/forms/textarea';
import { Group, Stack } from '@folio/components/ui/flex';
import { Text, Title } from '@folio/components/ui/text';
import { Badge } from '@folio/components/ui/badge';
import type { Tone } from '@folio/components/ui/tone';

const CLASSIFICATION_STATUS_PILL: Record<string, { labelKey: string; tone: Tone }> = {
  auto_accepted: {
    labelKey: 'aiClassification.autoClassified',
    tone: 'success',
  },
  manually_corrected: {
    labelKey: 'aiClassification.manuallyCorrected',
    tone: 'info',
  },
  pending_review: {
    labelKey: 'aiClassification.pendingReview',
    tone: 'warning',
  },
};

function AnalysisSection({
  title,
  status,
  children,
  reviewActionLabel,
}: {
  title: string;
  status?: ReactNode;
  children: ReactNode;
  reviewActionLabel?: string;
}) {
  const content = (
    <>
      <Group justify="between" gap={3} className="mb-1 min-h-9">
        <Title order={3} size="lg" tone="default" className="leading-7">
          {title}
        </Title>
        {status && <div className="shrink-0">{status}</div>}
      </Group>
      {children}
    </>
  );

  return (
    <section className="py-5 first:pt-0 last:pb-0">
      {reviewActionLabel ? (
        <DocumentReviewAction active label={reviewActionLabel}>
          {content}
        </DocumentReviewAction>
      ) : (
        content
      )}
    </section>
  );
}

function getDocumentType(document: DocumentViewerDocument): DocumentType {
  const extension = document.name.split('.').pop()?.toLowerCase();

  if (extension === 'pdf') return 'pdf';
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp'].includes(extension || '')) {
    return 'image';
  }

  if (document.signedUrl.includes('.pdf')) return 'pdf';
  if (/\.(png|jpe?g|gif|webp)/i.test(document.signedUrl)) return 'image';

  return 'unknown';
}

function ComplianceRuleRow({
  rule,
  t,
}: {
  rule: ValidityRuleResult;
  t: (key: string, opts?: Record<string, unknown>) => string;
}) {
  return (
    <Group align="start" gap={2.5} className="py-1.5">
      <span
        className={cn(
          'mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full',
          rule.passed ? 'bg-emerald-100' : 'bg-red-100',
        )}
      >
        {rule.passed ? (
          <CheckCircle2Icon className="h-3 w-3 text-emerald-700" />
        ) : (
          <XCircleIcon className="h-3 w-3 text-red-700" />
        )}
      </span>
      <div className="min-w-0">
        <Text inline size="sm" weight="medium" className="leading-5 text-slate-700">
          {t(`documents.validity.ruleTypes.${rule.ruleType}`, { defaultValue: rule.ruleType })}
        </Text>
        {rule.message && (
          <Text size="sm" weight="normal" tone="muted" className="mt-0.5 leading-5">
            {translateComplianceMessage(rule.message, rule.metadata, t)}
          </Text>
        )}
      </div>
    </Group>
  );
}

function ComplianceOverrideAction({
  status,
  needsReview = false,
  isOverriding,
  onConfirm,
  t,
}: {
  status: 'VALID' | 'INVALID';
  needsReview?: boolean;
  isOverriding?: boolean;
  onConfirm: () => void;
  t: (key: string, opts?: Record<string, unknown>) => string;
}) {
  const [open, setOpen] = useState(false);
  const targetLabel =
    status === 'VALID'
      ? t('documents.validity.override.markInvalid')
      : t('documents.validity.override.markValid');

  if (needsReview) {
    return (
      <Button
        type="button"
        variant="outline"
        size="sm"
        aria-busy={isOverriding}
        disabled={isOverriding}
        className={cn(
          'w-full rounded-lg',
          status === 'INVALID' &&
            'border-emerald-200 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-700',
        )}
        onClick={onConfirm}
      >
        {isOverriding ? (
          <Loader2Icon className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          <CheckIcon className="mr-2 h-4 w-4" />
        )}
        {status === 'INVALID'
          ? t('documents.validity.override.overrideValidityChecks')
          : targetLabel}
      </Button>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              aria-label={targetLabel}
              aria-busy={isOverriding}
              disabled={isOverriding}
              className="w-9 rounded p-0"
            >
              {isOverriding ? (
                <Loader2Icon className="w-3 h-3 animate-spin text-slate-500" />
              ) : (
                <ArrowRightLeftIcon className="w-3 h-3 text-slate-500" />
              )}
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        {!open && (
          <TooltipContent side="top">
            <p>{t('documents.validity.override.tooltip')}</p>
          </TooltipContent>
        )}
      </Tooltip>
      <PopoverContent
        align="start"
        aria-label={t('documents.validity.override.confirmTitle')}
        className="w-auto p-2"
      >
        <Button
          variant="outline"
          size="sm"
          className="text-xs"
          disabled={isOverriding}
          onClick={() => {
            onConfirm();
            setOpen(false);
          }}
        >
          {isOverriding ? (
            <Loader2Icon className="w-3 h-3 animate-spin mr-1" />
          ) : (
            <PenIcon className="w-3 h-3 mr-1" />
          )}
          {targetLabel}
        </Button>
      </PopoverContent>
    </Popover>
  );
}

function ConformityDecisionFooter({
  documentId,
  conformity,
  needsReview,
  onAccept,
  onReject,
  onCancel,
}: {
  documentId: string;
  conformity: DocumentConformityViewerState;
  needsReview: boolean;
  onAccept?: (documentId: string) => void;
  onReject?: (documentId: string, reason: string) => void;
  onCancel?: (documentId: string) => void;
}) {
  const { t } = useTranslation();
  const { reason, setReason, popoverOpen, setPopoverOpen, submit } = useConformityReasonPopover(
    documentId,
    onReject,
  );

  if (conformity.decision === 'ACCEPTED') {
    return (
      <Group
        justify="between"
        gap={3}
        className="border-t border-slate-200/60 bg-emerald-50 px-6 py-3.5"
      >
        <Text
          inline
          size="sm"
          weight="semibold"
          className="flex items-center gap-2 text-emerald-800"
        >
          <CheckIcon className="h-4 w-4" />
          {t('documentsListsPolicy.conformity.acceptedLegacy')}
        </Text>
        <Button
          variant="ghost"
          size="sm"
          className="rounded-lg text-emerald-800 hover:bg-emerald-100"
          onClick={() => onCancel?.(documentId)}
        >
          {t('documentsListsPolicy.conformity.cancelHint')}
        </Button>
      </Group>
    );
  }

  if (conformity.decision === 'REJECTED') {
    return (
      <Group
        justify="between"
        gap={3}
        className="border-t border-slate-200/60 bg-red-50 px-6 py-3.5"
      >
        <div className="min-w-0">
          <Text inline size="sm" weight="semibold" className="flex items-center gap-2 text-red-800">
            <XIcon className="h-4 w-4" />
            {t('documentsListsPolicy.conformity.rejected')}
          </Text>
          {conformity.reason && (
            <Text truncate size="xs" className="mt-0.5 text-red-700/80">
              {conformity.reason}
            </Text>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="rounded-lg text-red-800 hover:bg-red-100"
          onClick={() => onCancel?.(documentId)}
        >
          {t('documentsListsPolicy.conformity.cancelHint')}
        </Button>
      </Group>
    );
  }

  return (
    <div className="border-t border-slate-200/60 bg-white px-4 py-3.5">
      <Text size="xs" weight="bold" className="mb-2 uppercase tracking-wide text-slate-500">
        {t('documentsListsPolicy.conformity.decisionLabel')}
      </Text>
      <DocumentReviewAction
        active={needsReview}
        label={t('documentsListsPolicy.conformity.decisionLabel')}
        className="flex gap-2"
      >
        <Button
          className="flex-1 rounded-xl bg-emerald-600 text-white hover:bg-emerald-700"
          onClick={() => onAccept?.(documentId)}
        >
          <CheckIcon className="mr-1.5 h-4 w-4" />
          {t('documentsListsPolicy.conformity.acceptAction')}
        </Button>
        <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className="flex-1 rounded-xl border-red-200 text-red-700 hover:bg-red-50 hover:text-red-700"
            >
              <XIcon className="mr-1.5 h-4 w-4" />
              {t('documentsListsPolicy.conformity.rejectAction')}
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="flex w-[calc(100vw-2rem)] flex-col gap-2 sm:w-[22rem]"
            align="end"
            collisionPadding={16}
          >
            <Label
              htmlFor={`viewer-conformity-reason-${documentId}`}
              className="text-xs font-semibold"
            >
              {t('documentsListsPolicy.conformity.reasonLabel')}
            </Label>
            <Textarea
              id={`viewer-conformity-reason-${documentId}`}
              rows={3}
              value={reason}
              onChange={e => setReason(e.target.value)}
            />
            <Button
              variant="destructive"
              size="sm"
              className="w-full"
              disabled={!reason.trim()}
              onClick={submit}
            >
              {t('documentsListsPolicy.conformity.rejectSubmit')}
            </Button>
          </PopoverContent>
        </Popover>
      </DocumentReviewAction>
    </div>
  );
}

export function DocumentViewer({
  productId,
  review,
  onRetryAnalysis,
  isRetryingAnalysis,
  displayContext = 'broker',
  document,
  documents,
  onClose,
  onDocumentChange,
  onDelete,
  documentCustomCategorieMappings,
  uploadMode = 'category',
  extraction,
  extractionEnabled,
  onExtractionFeedback,
  isExtractionFeedbackPending,
  validity: compliance,
  validityEnabled: complianceEnabled,
  onValidityOverride: onComplianceOverride,
  isValidityOverriding: isComplianceOverriding,
  coherence,
  coherenceEntity = 'policy',
  fraud,
  fraudPending,
  onCorrectFraudVerdict,
  isCorrectingFraudVerdict,
  conformity,
  onAcceptConformity,
  onRejectConformity,
  onCancelConformity,
}: DocumentViewerProps) {
  const { t } = useTranslation();
  const canViewAnalysis = displayContext === 'broker';
  const [mobilePanel, setMobilePanel] = useState<'document' | 'analysis'>('document');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [localReportedFields, setLocalReportedFields] = useState<Map<string, ReportedFieldInfo>>(
    new Map(),
  );
  const [localDocumentReported, setLocalDocumentReported] = useState(false);

  const { data: existingFeedback, isPending: isExistingFeedbackPending } = useQuery({
    ...documentExtractionControllerGetExtractionFeedbackQueryOptions(document?.id ?? ''),
    enabled: canViewAnalysis && !!document?.id && !!extractionEnabled,
  });

  const reportedFields = useMemo(() => {
    const merged = new Map<string, ReportedFieldInfo>();
    if (existingFeedback?.feedback) {
      for (const item of existingFeedback.feedback) {
        if (item.feedbackType === 'FIELD_CORRECTION' && item.fieldKey) {
          merged.set(item.fieldKey, {
            correctedValue: item.correctedValue,
            comment: item.comment,
          });
        }
      }
    }
    for (const [key, info] of localReportedFields) {
      merged.set(key, info);
    }
    return merged;
  }, [existingFeedback, localReportedFields]);

  const isDocumentReported = useMemo(() => {
    if (localDocumentReported) return true;
    return existingFeedback?.feedback?.some(f => f.feedbackType === 'DOCUMENT_REJECTION') ?? false;
  }, [existingFeedback, localDocumentReported]);

  const documentRejectionComment = useMemo(() => {
    const rejection = existingFeedback?.feedback?.find(
      f => f.feedbackType === 'DOCUMENT_REJECTION',
    );
    return rejection?.comment;
  }, [existingFeedback]);

  const documentType = document ? getDocumentType(document) : 'unknown';

  const showExtraction = Boolean(
    canViewAnalysis && extractionEnabled && extraction && extraction.status !== 'SKIPPED',
  );

  const showCompliance = Boolean(
    canViewAnalysis && complianceEnabled && compliance && compliance.status !== 'SKIPPED',
  );

  const showFraud = Boolean(canViewAnalysis && (fraud || fraudPending));

  const aiClassification = useMemo(
    () =>
      document ? parseAIClassificationMetadata(document.metadata.aiclassification) : undefined,
    [document],
  );
  const classificationCategory =
    document?.category ??
    aiClassification?.suggestedCategory ??
    aiClassification?.suggestedcategory;
  const classificationStatusPill =
    aiClassification?.status && CLASSIFICATION_STATUS_PILL[aiClassification.status];
  const qualityFlags = aiClassification?.qualityFlags ?? [];
  const showControlResults = canViewAnalysis && (showCompliance || qualityFlags.length > 0);
  const showCoherence = Boolean(canViewAnalysis && coherence);
  const coherenceMismatchCount = coherence ? countCoherenceMismatches(coherence) : 0;

  // Only actionable when a handler was wired — policyholder view passes conformity read-only.
  const canDecideConformity = Boolean(
    canViewAnalysis &&
      conformity &&
      (onAcceptConformity || onRejectConformity || onCancelConformity),
  );

  const reviewReasons = canViewAnalysis
    ? (review?.reasons ??
      (canDecideConformity && conformity?.decision === 'PENDING' ? ['conformity'] : []))
    : [];
  const needsClassificationReview = reviewReasons.includes('classification');
  const needsValidityReview = reviewReasons.includes('validity');
  const needsCoherenceReview = reviewReasons.includes('coherence');
  const needsAnalysisRetry =
    reviewReasons.includes('analysis_failed') || reviewReasons.includes('validity_unavailable');
  const hasUnsafeDocument = reviewReasons.includes('unsafe_document');
  const hasPendingReview = reviewReasons.length > 0;
  const canConfirmClassification =
    needsClassificationReview &&
    aiClassification?.status === 'pending_review' &&
    documentCustomCategorieMappings?.some(({ category }) => category === classificationCategory);
  const showClassificationSelector = Boolean(
    document &&
      (document.type === 'supporting_document' ||
        document.type === 'non_delegated' ||
        document.type === 'generated') &&
      (hasOtherCategory(documentCustomCategorieMappings, document.category ?? undefined) ||
        canConfirmClassification) &&
      (uploadMode === 'batch' || displayContext === 'policyholder'),
  );
  const showClassification = Boolean(
    showClassificationSelector || (canViewAnalysis && aiClassification && classificationCategory),
  );

  // Skip the side column when there's nothing to show in it.
  const hasInfoPanel =
    showClassification ||
    showExtraction ||
    showControlResults ||
    showCoherence ||
    showFraud ||
    canDecideConformity ||
    hasPendingReview;

  const classificationAction =
    document &&
    showClassificationSelector &&
    documentCustomCategorieMappings &&
    (displayContext === 'policyholder' ? (
      <DocumentClassificationButton
        documentId={document.id}
        currentCategory={document.category ?? undefined}
        categories={documentCustomCategorieMappings}
        aiClassificationMetadata={aiClassification}
      />
    ) : (
      <DocumentClassificationSelector
        key={document.id}
        document={document}
        categories={documentCustomCategorieMappings}
        documents={documents}
        onDocumentChange={onDocumentChange ?? (() => {})}
        onClose={onClose}
        needsReview={needsClassificationReview}
      />
    ));
  const validityAction = onComplianceOverride &&
    compliance &&
    (compliance.status === 'VALID' || compliance.status === 'INVALID') && (
      <ComplianceOverrideAction
        key={document?.id}
        status={compliance.status}
        needsReview={needsValidityReview}
        isOverriding={isComplianceOverriding}
        onConfirm={() =>
          document &&
          onComplianceOverride(document.id, compliance.status === 'VALID' ? 'INVALID' : 'VALID')
        }
        t={t}
      />
    );
  const failedComplianceRules = compliance?.results.filter(({ passed }) => !passed) ?? [];
  const visibleComplianceRules = needsValidityReview
    ? failedComplianceRules
    : (compliance?.results ?? []);

  const currentIndex = document ? documents.findIndex(d => d.id === document.id) : -1;

  const handleNavigatePrev = () => {
    if (currentIndex > 0) {
      onDocumentChange?.(documents[currentIndex - 1]);
    }
  };

  const handleNavigateNext = () => {
    if (currentIndex < documents.length - 1) {
      onDocumentChange?.(documents[currentIndex + 1]);
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (!canViewAnalysis || !document || !onDelete) return;

    setIsDeleting(true);
    await onDelete(document);
    setShowDeleteDialog(false);
    onClose();
    setIsDeleting(false);
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        !document ||
        e.defaultPrevented ||
        (e.target instanceof HTMLElement && e.target.isContentEditable) ||
        (e.target instanceof Element &&
          e.target.closest('input, textarea, select, [role="tablist"]'))
      ) {
        return;
      }

      if ((e.key === 'ArrowLeft' || e.key === 'ArrowUp') && currentIndex > 0) {
        e.preventDefault();
        handleNavigatePrev();
      } else if (
        (e.key === 'ArrowRight' || e.key === 'ArrowDown') &&
        currentIndex < documents.length - 1
      ) {
        e.preventDefault();
        handleNavigateNext();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [document, currentIndex, documents.length, onClose]);

  // Reset extraction panel state when document changes
  useEffect(() => {
    setLocalReportedFields(new Map());
    setLocalDocumentReported(false);
    setMobilePanel('document');
  }, [document?.id]);

  const handleExtractionFeedback = useCallback(
    async (feedback: SubmitFeedbackBody) => {
      try {
        await onExtractionFeedback?.(feedback);
        if (feedback.feedbackType === 'FIELD_CORRECTION' && feedback.fieldKey) {
          setLocalReportedFields(prev => {
            const next = new Map(prev);
            next.set(feedback.fieldKey as string, {
              correctedValue: feedback.correctedValue,
              comment: feedback.comment,
            });
            return next;
          });
        } else if (feedback.feedbackType === 'DOCUMENT_REJECTION') {
          setLocalDocumentReported(true);
        }
      } catch (error) {
        // Submission failed — do not update local state so user can retry
        throw error;
      }
    },
    [onExtractionFeedback],
  );

  // Helper to render the header content only once
  const renderDocumentViewerHeader = () =>
    document && (
      <DocumentViewerHeader
        document={document}
        currentIndex={currentIndex}
        totalDocuments={documents.length}
        onNavigatePrev={handleNavigatePrev}
        onNavigateNext={handleNavigateNext}
        canNavigatePrev={currentIndex > 0}
        canNavigateNext={currentIndex < documents.length - 1}
        onDelete={canViewAnalysis && onDelete ? handleDeleteClick : undefined}
        onClose={onClose}
      />
    );

  return (
    <>
      <Sheet open={!!document} onOpenChange={open => !open && onClose()}>
        <Tabs
          value={mobilePanel}
          onValueChange={value => {
            if (value === 'document' || value === 'analysis') setMobilePanel(value);
          }}
          asChild
        >
          <SheetContent
            side="right"
            aria-describedby={undefined}
            className="w-full sm:max-w-6xl md:max-w-7xl overflow-hidden flex flex-col gap-0 p-0 [&>button]:hidden"
          >
            {document && (
              <>
                <SheetTitle className="sr-only">{document.name}</SheetTitle>
                <div className="border-b border-slate-200/60 bg-white px-5 py-4">
                  {renderDocumentViewerHeader()}
                </div>

                {hasInfoPanel && (
                  <TabsList
                    className="h-auto shrink-0 justify-start rounded-none border-b border-border bg-background px-5 md:hidden"
                    aria-label={t('documentReview.analysisResultLegacy')}
                  >
                    {(['document', 'analysis'] as const).map(panel => (
                      <TabsTrigger
                        key={panel}
                        value={panel}
                        aria-controls={`document-viewer-${panel}`}
                        className={cn(
                          'min-h-12 rounded-none border-b-2 border-transparent px-4 py-3 transition-colors data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-primary data-[state=active]:shadow-none focus-visible:ring-inset',
                          panel === 'analysis' && hasPendingReview && 'bg-amber-100 text-amber-950',
                        )}
                      >
                        {t(
                          panel === 'document'
                            ? 'documentReview.documentTab'
                            : 'documentReview.analysisTab',
                        )}
                        {panel === 'analysis' && hasPendingReview && (
                          <Text
                            inline
                            size="xs"
                            className="ml-2 rounded-full bg-amber-500 px-2 py-0.5 text-amber-950"
                          >
                            {t('documentReview.status.attention')}
                          </Text>
                        )}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                )}
                <Group align="stretch" gap={0} className="min-h-0 flex-1 overflow-hidden">
                  <TabsContent
                    value="document"
                    forceMount
                    id="document-viewer-document"
                    className={cn(
                      'mt-0 min-w-0 flex-1 overflow-auto bg-slate-50',
                      hasInfoPanel && mobilePanel === 'analysis' && 'hidden md:block',
                    )}
                  >
                    {documentType === 'pdf' && <PDFViewer url={document.signedUrl} />}

                    {documentType === 'image' && (
                      <Group align="stretch" className="h-full p-2">
                        <ImageViewer url={document.signedUrl} alt={document.name} />
                      </Group>
                    )}

                    {documentType === 'unknown' && (
                      <div className="p-3 md:p-4">
                        <Alert className="mx-auto mt-8 max-w-md">
                          <AlertTitle>Prévisualisation non disponible</AlertTitle>
                          <AlertDescription>
                            Ce type de fichier ne peut pas être affiché dans le navigateur.
                            <br />
                            <a
                              href={document.signedUrl}
                              download={document.name}
                              className="mt-2 inline-block text-primary underline hover:text-primary/80"
                            >
                              Télécharger le fichier
                            </a>
                          </AlertDescription>
                        </Alert>
                      </div>
                    )}
                  </TabsContent>

                  {hasInfoPanel && (
                    <TabsContent
                      value="analysis"
                      forceMount
                      id="document-viewer-analysis"
                      className={cn(
                        'mt-0 w-full min-w-0 flex-col bg-background md:w-96 md:shrink-0',
                        mobilePanel === 'document' ? 'hidden md:flex' : 'flex',
                      )}
                    >
                      <div className="flex-1 divide-y divide-border overflow-y-auto p-5">
                        {hasUnsafeDocument && (
                          <Text size="sm" tone="muted" className="mb-5">
                            {t('documentReview.replaceUnsafeDocument')}
                          </Text>
                        )}
                        {needsAnalysisRetry && (
                          <Stack gap={3} className="mb-5">
                            <Text size="sm" tone="muted">
                              {t('documentReview.analysisUnavailable')}
                            </Text>
                            {onRetryAnalysis && !hasUnsafeDocument && (
                              <DocumentReviewAction
                                active
                                label={t('documentReview.retryAnalysis')}
                              >
                                <Button
                                  variant="ghost"
                                  className="w-full text-amber-950 hover:bg-amber-100"
                                  disabled={isRetryingAnalysis}
                                  onClick={() => onRetryAnalysis(document.id)}
                                >
                                  <RotateCwIcon className="mr-2 h-4 w-4" />
                                  {t('documentReview.retryAnalysis')}
                                </Button>
                              </DocumentReviewAction>
                            )}
                          </Stack>
                        )}
                        {((needsClassificationReview && !classificationAction) ||
                          (needsValidityReview && !validityAction) ||
                          (needsAnalysisRetry && !onRetryAnalysis)) && (
                          <Text size="sm" tone="muted" className="mb-5">
                            {t('documentReview.reviewUnavailable')}
                          </Text>
                        )}
                        {showControlResults && (
                          <AnalysisSection
                            title={t('documentReview.analysisResultLegacy')}
                            reviewActionLabel={
                              needsValidityReview ? t('documentReview.reviewValidity') : undefined
                            }
                            status={
                              showCompliance &&
                              compliance && (
                                <Group gap={0} className="gap-1.5">
                                  <Badge
                                    variant="light"
                                    className="whitespace-nowrap"
                                    tone={
                                      needsValidityReview || compliance.status === 'FAILED'
                                        ? 'warning'
                                        : compliance.status === 'VALID'
                                          ? 'success'
                                          : compliance.status === 'INVALID'
                                            ? 'destructive'
                                            : 'muted'
                                    }
                                  >
                                    {needsValidityReview
                                      ? t('documents.validity.rules.toReview', {
                                          count: failedComplianceRules.length,
                                        })
                                      : t(
                                          `documents.validity.status.${compliance.status.toLowerCase()}`,
                                        )}
                                  </Badge>
                                  {!needsValidityReview && validityAction}
                                </Group>
                              )
                            }
                          >
                            {showCompliance && compliance?.override && (
                              <Stack
                                gap={1}
                                className="mb-3 border-b border-slate-100 pb-3 text-xs font-normal leading-5 text-muted-foreground"
                              >
                                <p>
                                  {t('documents.validity.override.overriddenManually')}{' '}
                                  {t('documents.validity.override.overriddenAt', {
                                    date: formatDate(new Date(compliance.override.overriddenAt)),
                                  })}
                                </p>
                                {compliance.override.originalStatus && (
                                  <p>
                                    {t('documents.validity.override.originalStatus', {
                                      status: t(
                                        `documents.validity.status.${compliance.override.originalStatus.toLowerCase()}`,
                                      ),
                                    })}
                                  </p>
                                )}
                              </Stack>
                            )}
                            {showCompliance && visibleComplianceRules.length > 0 && (
                              <div className="divide-y divide-slate-100">
                                {visibleComplianceRules.map((rule, index) => (
                                  <ComplianceRuleRow key={index} rule={rule} t={t} />
                                ))}
                              </div>
                            )}
                            {needsValidityReview && validityAction && (
                              <div className="mt-3 border-t border-slate-100 pt-3">
                                {validityAction}
                              </div>
                            )}
                            {qualityFlags.length > 0 && (
                              <div className="mt-3 rounded-lg bg-amber-50 px-3 py-2.5">
                                <Group gap={2} className="text-amber-800">
                                  <AlertTriangleIcon className="h-4 w-4 shrink-0" />
                                  <Text size="sm" weight="medium" className="leading-5">
                                    {t('aiClassification.qualityAlerts')}
                                  </Text>
                                </Group>
                                <Stack asChild gap={1}>
                                  <ul className="mt-2 pl-6 text-sm leading-5 text-muted-foreground">
                                    {qualityFlags.map(flag => (
                                      <li key={flag} className="flex items-start gap-2">
                                        <span
                                          aria-hidden
                                          className="mt-2 h-1 w-1 shrink-0 rounded-full bg-amber-500"
                                        />
                                        <span>
                                          {t(`aiClassification.qualityFlags.${flag}`, flag)}
                                        </span>
                                      </li>
                                    ))}
                                  </ul>
                                </Stack>
                              </div>
                            )}
                          </AnalysisSection>
                        )}

                        {showClassification && (
                          <AnalysisSection
                            title={t('aiClassification.sectionTitle')}
                            status={
                              <Group gap={0} className="gap-1.5">
                                {classificationStatusPill && (
                                  <Badge
                                    variant="light"
                                    tone={classificationStatusPill.tone}
                                    className="whitespace-nowrap"
                                  >
                                    {t(classificationStatusPill.labelKey)}
                                  </Badge>
                                )}
                                {!needsClassificationReview && classificationAction}
                              </Group>
                            }
                          >
                            <Text size="md" weight="normal" tone="default" className="leading-6">
                              {getDocumentCategoryLabel(
                                classificationCategory ?? 'uncategorized',
                                documentCustomCategorieMappings,
                                t,
                              )}
                            </Text>
                            {needsClassificationReview && classificationAction && (
                              <DocumentReviewAction
                                active
                                label={t('documentReview.reviewCategory')}
                                className="mt-3"
                              >
                                {classificationAction}
                              </DocumentReviewAction>
                            )}
                          </AnalysisSection>
                        )}

                        {showFraud && (
                          <FraudAnalysisSection
                            fraud={fraud}
                            pending={fraudPending}
                            onCorrectVerdict={
                              onCorrectFraudVerdict && document
                                ? correction => onCorrectFraudVerdict(document.id, correction)
                                : undefined
                            }
                            isCorrectingVerdict={isCorrectingFraudVerdict}
                          />
                        )}

                        {showExtraction && (
                          <AnalysisSection
                            title={t('documents.extraction.title')}
                            status={
                              isDocumentReported && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Text
                                      inline
                                      size="xs"
                                      weight="medium"
                                      className="inline-flex cursor-default items-center gap-1 text-amber-600"
                                    >
                                      <FlagIcon className="h-3 w-3" />
                                      {t('documents.extraction.feedback.documentReported')}
                                    </Text>
                                  </TooltipTrigger>
                                  {documentRejectionComment && (
                                    <TooltipContent className="max-w-xs">
                                      {documentRejectionComment}
                                    </TooltipContent>
                                  )}
                                </Tooltip>
                              )
                            }
                          >
                            <ExtractionFieldsDisplay
                              extraction={extraction as GetDocumentExtractionOutput}
                              extractionEnabled={extractionEnabled}
                              onFeedback={
                                onExtractionFeedback && !isExistingFeedbackPending
                                  ? handleExtractionFeedback
                                  : undefined
                              }
                              isFeedbackPending={
                                isExtractionFeedbackPending && !!onExtractionFeedback
                              }
                              reportedFields={reportedFields}
                              isDocumentReported={isDocumentReported}
                            />
                          </AnalysisSection>
                        )}

                        {showCoherence && coherence && (
                          <AnalysisSection
                            title={t(`documents.coherence.${coherenceEntity}.title`)}
                            reviewActionLabel={
                              needsCoherenceReview ? t('documentReview.reviewCoherence') : undefined
                            }
                            status={
                              coherenceMismatchCount > 0 && (
                                <Badge variant="light" tone="warning" className="whitespace-nowrap">
                                  {t('documents.coherence.mismatches', {
                                    count: coherenceMismatchCount,
                                  })}
                                </Badge>
                              )
                            }
                          >
                            <DocumentCoherenceTable
                              coherence={coherence}
                              entity={coherenceEntity}
                              productId={productId}
                            />
                          </AnalysisSection>
                        )}
                      </div>

                      {canDecideConformity && conformity && (
                        <ConformityDecisionFooter
                          key={document.id}
                          documentId={document.id}
                          conformity={conformity}
                          needsReview={reviewReasons.includes('conformity')}
                          onAccept={onAcceptConformity}
                          onReject={onRejectConformity}
                          onCancel={onCancelConformity}
                        />
                      )}
                    </TabsContent>
                  )}
                </Group>
              </>
            )}
          </SheetContent>
        </Tabs>
      </Sheet>

      {canViewAnalysis && document && (
        <DeleteDocumentDialog
          open={showDeleteDialog}
          onOpenChange={setShowDeleteDialog}
          onConfirm={handleDeleteConfirm}
          documentName={document.name}
          isDeleting={isDeleting}
        />
      )}
    </>
  );
}
